class LCPriceSlider {
  constructor(container) {
    // Dawn replaces filter markup after every AJAX filter update. Check for
    // the actual slider instead of a persistent data flag so it is rebuilt
    // whenever Shopify refreshes the price filter.
    if (container.querySelector('.lc-price-slider')) return;

    const minField = container.querySelector(
      'input[name="filter.v.price.gte"]'
    );
    const maxField = container.querySelector(
      'input[name="filter.v.price.lte"]'
    );

    if (!minField || !maxField) return;

    const parsePrice = (value) => {
      const normalized = String(value ?? '')
        .trim()
        .replace(/,/g, '')
        .replace(/[^0-9.-]/g, '');

      return Number.parseFloat(normalized);
    };

    // Dawn 15.5 stores the maximum price in data-max, not in the standard
    // HTML max attribute.
    const limit = parsePrice(
      maxField.dataset.max ||
        minField.dataset.max ||
        maxField.placeholder ||
        maxField.max
    );

    if (!limit || limit <= 0) return;

    const priceStep = 0.25;
    const sliderLimit = Math.ceil(limit / priceStep) * priceStep;
    const highestMinimum = Math.floor(limit / priceStep) * priceStep;
    const snapToStep = (value) => Math.round(value / priceStep) * priceStep;

    const selectedMin = Math.min(
      snapToStep(parsePrice(minField.value) || 0),
      highestMinimum
    );
    const selectedMax = minField.value || maxField.value
      ? Math.min(snapToStep(parsePrice(maxField.value) || sliderLimit), sliderLimit)
      : sliderLimit;

    container.classList.add('lc-price-slider-ready');

    const slider = document.createElement('div');
    slider.className = 'lc-price-slider';
    slider.innerHTML = `
      <div class="lc-price-slider__values">
        <span class="lc-price-min-value"></span>
        <span class="lc-price-max-value"></span>
      </div>

      <div class="lc-price-slider__track">
        <input
          class="lc-price-range lc-price-range--min"
          type="range"
          min="0"
          max="${sliderLimit}"
          step="0.25"
          value="${selectedMin}"
          aria-label="Minimum price"
        >

        <input
          class="lc-price-range lc-price-range--max"
          type="range"
          min="0"
          max="${sliderLimit}"
          step="0.25"
          value="${selectedMax}"
          aria-label="Maximum price"
        >
      </div>
    `;

    container.prepend(slider);

    const minRange = slider.querySelector('.lc-price-range--min');
    const maxRange = slider.querySelector('.lc-price-range--max');
    const minLabel = slider.querySelector('.lc-price-min-value');
    const maxLabel = slider.querySelector('.lc-price-max-value');

    const formatPrice = (value) => {
      if (Number(value) === 0) return '0 KD';

      return `${Number(value).toLocaleString('en-KW', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })} KD`;
    };

    const updateSlider = () => {
      let min = Number(minRange.value);
      let max = Number(maxRange.value);

      if (min > highestMinimum) {
        min = highestMinimum;
        minRange.value = highestMinimum;
      }

      if (min > max) {
        if (document.activeElement === minRange) {
          min = max;
          minRange.value = max;
        } else {
          max = min;
          maxRange.value = min;
        }
      }

      minField.value = min === 0 ? '' : min;
      maxField.value = max >= sliderLimit ? '' : Math.min(max, limit);

      minLabel.textContent = formatPrice(min);
      maxLabel.textContent = formatPrice(max);

      slider.style.setProperty('--price-min', `${(min / sliderLimit) * 100}%`);
      slider.style.setProperty('--price-max', `${(max / sliderLimit) * 100}%`);
    };

    const applyFilter = (event) => {
      updateSlider();

      // Keep Dawn's built-in validation state synchronized, then trigger its
      // normal debounced AJAX filtering from the field that changed.
      minField.dispatchEvent(new Event('change', { bubbles: true }));
      maxField.dispatchEvent(new Event('change', { bubbles: true }));

      const changedField = event.currentTarget === minRange ? minField : maxField;
      changedField.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const previewRange = (event) => {
      // Dawn listens for every bubbling input event inside the filter form.
      // Keep dragging smooth and submit only after the handle is released.
      event.stopPropagation();
      updateSlider();
    };

    minRange.addEventListener('input', previewRange);
    maxRange.addEventListener('input', previewRange);

    minRange.addEventListener('change', applyFilter);
    maxRange.addEventListener('change', applyFilter);

    minField.addEventListener('change', () => {
      minRange.value = Math.min(
        snapToStep(parsePrice(minField.value) || 0),
        highestMinimum
      );
      updateSlider();
    });

    maxField.addEventListener('change', () => {
      maxRange.value = maxField.value
        ? Math.min(snapToStep(parsePrice(maxField.value)), sliderLimit)
        : sliderLimit;
      updateSlider();
    });

    updateSlider();
  }
}

function initializeLCPriceSliders() {
  document.querySelectorAll('.facets__price').forEach((container) => {
    new LCPriceSlider(container);
  });
}

document.addEventListener('DOMContentLoaded', initializeLCPriceSliders);

const lcSliderObserver = new MutationObserver(() => {
  initializeLCPriceSliders();
});

lcSliderObserver.observe(document.body, {
  childList: true,
  subtree: true
});
